import { makeStyles, Spinner, tokens } from '@fluentui/react-components';
import { Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';
import { useCallback, useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { useI18n } from '../lib/i18n/hooks';

// Key used to persist "just updated" state across page reload
const PWA_JUST_UPDATED_KEY = 'pwa-just-updated';
// Check for updates every 60 seconds
const UPDATE_CHECK_INTERVAL_MS = 60 * 1000;

interface PWAUpdateToastProps {
  toasterId: string;
}

const useStyles = makeStyles({
  updateBanner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export function PWAUpdateToast({ toasterId }: PWAUpdateToastProps) {
  const styles = useStyles();
  const { dispatchToast } = useToastController(toasterId);
  const { t } = useI18n();
  const [isUpdating, setIsUpdating] = useState(false);

  // Show "app updated" toast if we just refreshed after an update
  useEffect(() => {
    const justUpdated = globalThis.sessionStorage?.getItem(PWA_JUST_UPDATED_KEY);
    if (justUpdated) {
      globalThis.sessionStorage.removeItem(PWA_JUST_UPDATED_KEY);
      dispatchToast(
        <Toast>
          <ToastTitle>{t('pwa.updated-title', 'App Updated')}</ToastTitle>
          <ToastBody>{t('pwa.updated-body', 'The application has been updated to the latest version.')}</ToastBody>
        </Toast>,
        {
          intent: 'success',
          timeout: 5000,
        },
      );
    }
  }, [dispatchToast, t]);

  const handleNeedRefresh = useCallback(() => {
    // Show the updating banner
    setIsUpdating(true);
  }, []);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Set up periodic update checks
      if (registration) {
        setInterval(async () => {
          try {
            await registration.update();
          } catch {
            // Service worker update check failed (e.g. redirect or network error on Cloudflare Pages)
            // This is non-critical — the app continues to work with the current cached version
          }
        }, UPDATE_CHECK_INTERVAL_MS);
      }
    },
    onNeedRefresh: handleNeedRefresh,
    onOfflineReady() {
      // App is ready to work offline
      console.log('App ready to work offline');
    },
  });

  // When needRefresh becomes true, trigger the update
  useEffect(() => {
    if (needRefresh && isUpdating) {
      // Mark that we're updating so we can show success toast after reload
      globalThis.sessionStorage?.setItem(PWA_JUST_UPDATED_KEY, 'true');
      // Trigger the service worker update (this will cause skipWaiting + claim)
      void updateServiceWorker(true);
    }
  }, [needRefresh, isUpdating, updateServiceWorker]);

  useEffect(() => {
    // Listen for when the new service worker has taken control
    const handleControllerChange = () => {
      // The session storage flag is already set, just reload
      globalThis.location.reload();
    };

    if (globalThis.navigator?.serviceWorker) {
      globalThis.navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      return () => {
        globalThis.navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  // Show the updating banner when an update is in progress
  if (isUpdating) {
    return (
      <div className={styles.updateBanner}>
        <Spinner size="tiny" appearance="inverted" />
        <span>{t('pwa.updating', 'Updating application...')}</span>
      </div>
    );
  }

  return null;
}
